import React from 'react'
import type { StoreFilters } from '../../types/store'
import { normalizeStoreFilters, DEFAULT_STORE_FILTERS } from '../../types/store'

type Props = {
  open: boolean
  onClose: () => void
  filters: Partial<StoreFilters>
  onApply: (next: StoreFilters) => void
  onClear?: () => void
}

export default function StoreFilterDrawer({ open, onClose, filters, onApply, onClear }: Props) {
  const [local, setLocal] = React.useState<StoreFilters>(normalizeStoreFilters(filters))
  React.useEffect(() => setLocal(normalizeStoreFilters(filters)), [filters])

  function apply() { onApply(normalizeStoreFilters(local)) }
  function clear() {
    setLocal({ ...DEFAULT_STORE_FILTERS })
    onClear?.()
  }

  return (
    <div aria-hidden={!open}>
      <div
        className={`fixed inset-0 bg-black/50 transition-opacity ${open ? 'opacity-100' : 'pointer-events-none opacity-0'} z-40`}
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        className={`fixed right-0 top-0 h-full w-[360px] md:w-[420px] bg-neutral-900 border-l border-white/10 shadow-2xl z-50 transform transition-transform ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="sticky top-0 z-10 bg-neutral-900 border-b border-white/10 px-3 py-2">
          <div className="flex items-center gap-2">
            <div className="text-base font-extrabold">Filters</div>
            <button onClick={onClose} className="ml-auto text-white/80 hover:text-white">✕</button>
          </div>
        </div>

        <div className="p-4 space-y-6">
          {/* Category */}
          <div>
            <div className="text-sm text-white/70 mb-2">Category</div>
          <select
            value={local.category ?? ''}
            onChange={(e) => setLocal((p) => ({ ...p, category: e.target.value || null }))}
            className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none"
          >
            <option value="">All</option>
            <option value="Merch">Merch</option>
            <option value="Electronics">Electronics</option>
            <option value="Home & Garden">Home &amp; Garden</option>
            <option value="Clothing">Clothing</option>
            <option value="Toys">Toys</option>
            <option value="Vehicles">Vehicles</option>
            <option value="Art & Collectibles">Art &amp; Collectibles</option>
            <option value="Music">Music</option>
          </select>
          </div>

          {/* Condition */}
          <div>
            <div className="text-sm text-white/70 mb-2">Condition</div>
            <div className="flex flex-wrap gap-2">
              {([
                { key: 'new', label: 'New' },
                { key: 'used', label: 'Used' },
              ] as const).map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setLocal((p) => ({ ...p, condition: p.condition === (opt.key as any) ? null : (opt.key as any) }))}
                  className={`px-3 py-1.5 rounded-full border text-xs ${
                    local.condition === (opt.key as any)
                      ? 'bg-fuchsia-900/30 border-fuchsia-700/50 text-fuchsia-200'
                      : 'bg-white/5 border-white/10 text-white/85 hover:bg-white/10'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Availability */}
          <div>
            <div className="text-sm text-white/70 mb-2">Availability</div>
            <button
              onClick={() => setLocal((p) => ({ ...p, inStockOnly: !p.inStockOnly }))}
              className={`px-3 py-1.5 rounded-full border text-xs ${
                local.inStockOnly ? 'bg-fuchsia-900/30 border-fuchsia-700/50 text-fuchsia-200' : 'bg-white/5 border-white/10 text-white/85 hover:bg-white/10'
              }`}
            >
              In stock only
            </button>
          </div>

          {/* Price */}
          <div>
            <div className="text-sm text-white/70 mb-2">Price (PIXE)</div>
            <div className="flex flex-wrap gap-2">
              {([
                // Match mobile tiers: u25 => max 25 only (no lower bound)
                { key: 'u25', label: 'Under 25', min: null, max: 25 },
                { key: '25_50', label: '25–50', min: 25, max: 50 },
                { key: '50_100', label: '50–100', min: 50, max: 100 },
                { key: '100_250', label: '100–250', min: 100, max: 250 },
                { key: '250p', label: '250+', min: 250, max: null },
              ] as const).map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setLocal((p) => ({ ...p, priceTier: opt.key as any, priceMin: opt.min as any, priceMax: opt.max as any }))}
                  className={`px-3 py-1.5 rounded-full border text-xs ${
                    local.priceTier === (opt.key as any)
                      ? 'bg-fuchsia-900/30 border-fuchsia-700/50 text-fuchsia-200'
                      : 'bg-white/5 border-white/10 text-white/85 hover:bg-white/10'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <input
                value={local.priceMin != null ? String(local.priceMin) : ''}
                onChange={(e) => setLocal((p) => ({ ...p, priceMin: e.target.value ? Number(e.target.value) : null, priceTier: null }))}
                placeholder="Min"
                className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none"
                inputMode="decimal"
              />
              <input
                value={local.priceMax != null ? String(local.priceMax) : ''}
                onChange={(e) => setLocal((p) => ({ ...p, priceMax: e.target.value ? Number(e.target.value) : null, priceTier: null }))}
                placeholder="Max"
                className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none"
                inputMode="decimal"
              />
            </div>
          </div>

          {/* Sort */}
          <div>
            <div className="text-sm text-white/70 mb-2">Sort</div>
            <div className="flex flex-wrap gap-2">
              {([
                { key: 'newest', label: 'Newest' },
                { key: 'popular', label: 'Popular' },
                { key: 'price_asc', label: 'Price ↑' },
                { key: 'price_desc', label: 'Price ↓' },
              ] as const).map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setLocal((p) => ({ ...p, sort: opt.key as any }))}
                  className={`px-3 py-1.5 rounded-full border text-xs ${
                    local.sort === (opt.key as any)
                      ? 'bg-fuchsia-900/30 border-fuchsia-700/50 text-fuchsia-200'
                      : 'bg-white/5 border-white/10 text-white/85 hover:bg-white/10'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="pt-2 flex items-center justify-between">
            <button onClick={clear} className="text-sm text-white/70 hover:text-white">Clear</button>
            <button onClick={apply} className="px-3 py-2 rounded-lg bg-brand/80 hover:bg-brand text-sm font-semibold">Apply</button>
          </div>
        </div>
      </div>
    </div>
  )
}
